import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LogInModal from 'flarum/forum/components/LogInModal';
import LoungeModerationModal from './LoungeModerationModal';
import pakt21ApiUrl from '../../common/pakt21ApiUrl';

export default class LoungePanel extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    this.messages = [];
    this.content = '';
    this.loading = true;
    this.loadingMessages = false;
    this.submitting = false;
    this.loadError = false;
    this.sendError = null;
    this.expanded = false;

    this.loadMessages(true);
    this.refreshTimer = window.setInterval(() => this.loadMessages(false), 5000);
  }

  onremove() {
    window.clearInterval(this.refreshTimer);
  }

  apiUrl() {
    return pakt21ApiUrl(app.forum.attribute('apiUrl'), '/pakt21/lounge/messages');
  }

  loadMessages(scrollToBottom) {
    if (this.loadingMessages) {
      return Promise.resolve();
    }

    this.loadingMessages = true;

    return app
      .request({
        method: 'GET',
        url: this.apiUrl(),
        // Odświeżanie w tle ma pokazać jeden komunikat wewnątrz Lounge,
        // a nie globalny czerwony alert Flarum co pięć sekund.
        errorHandler: () => {},
      })
      .then((payload) => {
        this.messages = this.parseMessages(payload);
        this.loadError = false;

        if (scrollToBottom) {
          this.scrollToBottom();
        }
      })
      .catch(() => {
        this.loadError = true;
      })
      .finally(() => {
        this.loadingMessages = false;
        this.loading = false;
        m.redraw();
      });
  }

  submit(event) {
    event.preventDefault();

    const content = this.content.trim();

    if (!content || this.submitting) {
      return;
    }

    this.submitting = true;
    this.sendError = null;

    app
      .request({
        method: 'POST',
        url: this.apiUrl(),
        body: {
          data: {
            type: 'pakt21-lounge-messages',
            attributes: { content },
          },
        },
        // Błąd wysłania jest renderowany przy formularzu. Nie dublujemy go
        // globalnym alertem Flarum, szczególnie uciążliwym na telefonie.
        errorHandler: () => {},
      })
      .then((payload) => {
        const [message] = this.parseMessages(payload);

        if (message) {
          this.messages = [...this.messages.filter((item) => item.id !== message.id), message];
        }

        this.content = '';
        this.scrollToBottom();
      })
      .catch((error) => {
        this.sendError =
          error?.response?.errors?.[0]?.detail ||
          error?.errors?.[0]?.detail ||
          app.translator.trans('pakt21-beta.forum.lounge.send_error');
      })
      .finally(() => {
        this.submitting = false;
        m.redraw();
      });
  }

  handleComposerKeydown(event) {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) {
      return;
    }

    event.preventDefault();
    this.submit(event);
  }

  parseMessages(payload) {
    const users = new Map(
      (payload.included || [])
        .filter((item) => item.type === 'users')
        .map((item) => [String(item.id), item.attributes])
    );
    const data = Array.isArray(payload.data) ? payload.data : [payload.data].filter(Boolean);

    return data.map((item) => {
      const userId = item.relationships?.user?.data?.id;

      return {
        id: String(item.id),
        content: item.attributes.content,
        createdAt: item.attributes.createdAt,
        canHide: Boolean(item.attributes.canHide),
        user: users.get(String(userId)) || {
          displayName: app.translator.trans('core.lib.username.deleted_text'),
          avatarUrl: null,
        },
      };
    });
  }

  scrollToBottom() {
    window.setTimeout(() => {
      const list = document.querySelector('.Pakt21Lounge-messages');

      if (list) {
        list.scrollTop = list.scrollHeight;
      }
    }, 0);
  }

  formatTimestamp(value) {
    return new Intl.DateTimeFormat(document.documentElement.lang || 'pl', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  openChat() {
    if (!app.session.user) {
      app.modal.show(LogInModal);
      return;
    }

    this.expanded = true;
  }

  hideMessage(message) {
    app.modal.show(LoungeModerationModal, {
      message,
      onHidden: (messageId) => {
        this.messages = this.messages.filter((item) => item.id !== messageId);
        m.redraw();
      },
    });
  }

  view() {
    const visibleMessages = this.expanded ? this.messages : this.messages.slice(-4);

    return (
      <section id="pakt21-live-chat" className="Pakt21Lounge" aria-labelledby="pakt21-lounge-title">
        <div className={`Pakt21Lounge-card ${this.expanded ? 'Pakt21Lounge-card--expanded' : ''}`.trim()}>
          <header className="Pakt21Lounge-heading">
            <div className="Pakt21Lounge-headingMain">
              <span className="Pakt21Lounge-status" aria-hidden="true" />
              <span className="Pakt21Lounge-kicker">
                {app.translator.trans('pakt21-beta.forum.lounge.kicker')}
              </span>
              <span className="Pakt21Lounge-communityStatus">
                {app.translator.trans('pakt21-beta.forum.lounge.community_status')}
              </span>
            </div>
            <button
              type="button"
              className="Button Button--link Pakt21Lounge-openLink"
              onclick={() => (this.expanded ? (this.expanded = false) : this.openChat())}
            >
              {app.translator.trans(
                this.expanded
                  ? 'pakt21-beta.forum.lounge.close_chat'
                  : 'pakt21-beta.forum.lounge.open_chat'
              )}{' '}
              <span aria-hidden="true">→</span>
            </button>
          </header>

          <div className="Pakt21Lounge-body">
            <div className="Pakt21Lounge-conversation">
              {this.loading ? (
                <p className="Pakt21Lounge-feedback">
                  {app.translator.trans('pakt21-beta.forum.lounge.loading')}
                </p>
              ) : visibleMessages.length ? (
                <ol className="Pakt21Lounge-messages" aria-live="polite">
                  {visibleMessages.map((message) => (
                    <li className="Pakt21Lounge-message" key={message.id}>
                      <span className="Pakt21Lounge-avatar" aria-hidden="true">
                        {message.user.avatarUrl ? (
                          <img src={message.user.avatarUrl} alt="" />
                        ) : (
                          String(message.user.displayName || '?').charAt(0).toUpperCase()
                        )}
                      </span>

                      <div className="Pakt21Lounge-messageBody">
                        <div className="Pakt21Lounge-messageMeta">
                          <strong>{message.user.displayName}</strong>
                          <time datetime={message.createdAt}>{this.formatTimestamp(message.createdAt)}</time>
                          {message.canHide && (
                            <button
                              type="button"
                              className="Button Button--link Pakt21Lounge-hideMessage"
                              onclick={() => this.hideMessage(message)}
                            >
                              {app.translator.trans('pakt21-beta.forum.lounge_moderation.hide_button')}
                            </button>
                          )}
                        </div>
                        <p>{message.content}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="Pakt21Lounge-feedback">
                  {app.translator.trans('pakt21-beta.forum.lounge.empty')}
                </p>
              )}

              {this.loadError && (
                <p className="Pakt21Lounge-error" role="status">
                  {app.translator.trans('pakt21-beta.forum.lounge.load_error')}
                </p>
              )}
            </div>

            {!this.expanded && (
              <div className="Pakt21Lounge-invitation">
                <span className="Pakt21Lounge-invitationIcon" aria-hidden="true">•••</span>
                <p>{app.translator.trans('pakt21-beta.forum.lounge.invitation')}</p>
                <Button className="Button Button--primary" onclick={() => this.openChat()}>
                  {app.translator.trans('pakt21-beta.forum.lounge.enter_chat')}
                </Button>
              </div>
            )}
          </div>

          {this.expanded &&
            (app.session.user ? (
                <form className="Pakt21Lounge-composer" onsubmit={(event) => this.submit(event)}>
                  <label className="sr-only" for="pakt21-lounge-message">
                    {app.translator.trans('pakt21-beta.forum.lounge.placeholder')}
                  </label>
                  <textarea
                    id="pakt21-lounge-message"
                    maxlength="500"
                    rows="2"
                    value={this.content}
                    placeholder={app.translator.trans('pakt21-beta.forum.lounge.placeholder')}
                    oninput={(event) => {
                      this.content = event.target.value;
                      this.sendError = null;
                    }}
                    onkeydown={(event) => this.handleComposerKeydown(event)}
                  />

                  <div className="Pakt21Lounge-composerActions">
                    <span>{app.translator.trans('pakt21-beta.forum.lounge.max_length')}</span>
                    <Button
                      className="Button Button--primary"
                      type="submit"
                      loading={this.submitting}
                      disabled={!this.content.trim()}
                    >
                      {app.translator.trans('pakt21-beta.forum.lounge.send')}
                    </Button>
                  </div>

                  {this.sendError && (
                    <p className="Pakt21Lounge-error" role="alert">{this.sendError}</p>
                  )}
                </form>
              ) : (
                <div className="Pakt21Lounge-guest">
                  <span>{app.translator.trans('pakt21-beta.forum.lounge.guest')}</span>
                  <Button
                    className="Button Button--primary"
                    onclick={() => app.modal.show(LogInModal)}
                  >
                    {app.translator.trans('pakt21-beta.forum.lounge.log_in')}
                  </Button>
                </div>
              ))}
        </div>
      </section>
    );
  }
}
